import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { LuArchiveX, LuCheck, LuLoader, LuPencil, LuUsers, LuX } from 'react-icons/lu';

import type { AccountOnboardingView } from '@ndvr/customer-api';

import { SimpleSortableHeader } from '@/components/SimpleSortableHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSimpleTableSort } from '@/hooks/useSimpleTableSort';
import { formatDate, getHouseholdName, titleCase } from 'utils';
import {
    checkAccountOwnerDuplicates,
    useAssignAccountToHouseholdAsAdmin,
} from 'views/dashboard2/AccountOnboarding/account-onboarding-queries';
import { PotentialDuplicateDialog } from 'views/person/PotentialDuplicateDialog';
import { usePotentialPersonDuplicates } from 'views/person/usePotentialPersonDuplicates';

import { useAllTenantsQuery } from '../internalUserProfiles/internal-user-profile-queries';
import {
    AccountOnboardingAdminQueryKeys,
    useAssignAccountToTenantMutation,
    useDismissAccountOnboardingMutation,
    useHouseholdsByTenantQuery,
    useNewAccountsNotYetOnboardedQuery,
} from './account-onboarding-admin-queries';

const getAccountOwnerName = (account: AccountOnboardingView): string => {
    const owner = account.accountOwners[0];
    if (!owner) return '—';

    if (owner.orgName) {
        return owner.orgName;
    }

    const parts = [owner.firstName, owner.middleName, owner.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
};

const getCustodianName = (account: AccountOnboardingView): string =>
    account.custodyType ? titleCase(account.custodyType) : '—';

type SortColumn = 'tradeDate' | 'accountNumber' | 'accountType' | 'custodyType' | 'accountOwner';

export const AccountOnboardingAdmin = () => {
    const { data: accounts = [], isLoading: isLoadingAccounts } = useNewAccountsNotYetOnboardedQuery();
    const { data: tenants = [], isLoading: isLoadingTenants } = useAllTenantsQuery();
    const assignMutation = useAssignAccountToTenantMutation();
    const dismissMutation = useDismissAccountOnboardingMutation();
    const [editingAccountNumber, setEditingAccountNumber] = useState<string | null>(null);
    const [selectedTenantGUID, setSelectedTenantGUID] = useState<string>('');
    const [householdModalAccount, setHouseholdModalAccount] = useState<AccountOnboardingView | null>(null);
    const [dismissModalAccount, setDismissModalAccount] = useState<AccountOnboardingView | null>(null);
    const {
        sortedData: sortedAccounts,
        sortColumn,
        sortDirection,
        handleSort,
    } = useSimpleTableSort<AccountOnboardingView, SortColumn>({
        data: accounts,
        sortFunctions: {
            tradeDate: (a, b) => {
                const aTime = a.tradeDate ? new Date(a.tradeDate).getTime() : 0;
                const bTime = b.tradeDate ? new Date(b.tradeDate).getTime() : 0;
                return aTime - bTime;
            },
            accountNumber: (a, b) => a.accountNumber.localeCompare(b.accountNumber),
            accountType: (a, b) => titleCase(a.accountType).localeCompare(titleCase(b.accountType)),
            custodyType: (a, b) => getCustodianName(a).localeCompare(getCustodianName(b)),
            accountOwner: (a, b) => getAccountOwnerName(a).localeCompare(getAccountOwnerName(b)),
        },
        initialSort: {
            column: 'tradeDate',
            direction: 'desc',
        },
    });

    const handleEditClick = (account: AccountOnboardingView) => {
        setEditingAccountNumber(account.accountNumber);
        setSelectedTenantGUID(account.tenantGUID ?? '');
    };

    const handleSave = (accountNumber: string) => {
        if (!selectedTenantGUID) return;

        assignMutation.mutate(
            { accountNumber, tenantGUID: selectedTenantGUID },
            {
                onSuccess: () => {
                    setEditingAccountNumber(null);
                    setSelectedTenantGUID('');
                },
                onError: () => {
                    // Keep UI in edit mode so user can retry
                    // Error toast is already shown by the mutation hook
                },
            },
        );
    };

    const handleCancel = () => {
        setEditingAccountNumber(null);
        setSelectedTenantGUID('');
    };

    const getTenantName = (tenantGUID: string | null) => {
        if (!tenantGUID) return '—';
        const tenant = tenants.find((t) => t.tenantGUID === tenantGUID);
        return tenant?.name ?? '—';
    };

    const handleDismiss = () => {
        if (!dismissModalAccount) return;

        dismissMutation.mutate(dismissModalAccount.accountNumber, {
            onSuccess: () => setDismissModalAccount(null),
        });
    };

    return (
        <Card>
            <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <LuUsers className="h-4 w-4" />
                    Account Tenant Assignments
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <SimpleSortableHeader
                                column="tradeDate"
                                label="Date"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SimpleSortableHeader
                                column="accountNumber"
                                label="Account Number"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SimpleSortableHeader
                                column="accountType"
                                label="Account Type"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SimpleSortableHeader
                                column="custodyType"
                                label="Custodian"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SimpleSortableHeader
                                column="accountOwner"
                                label="Account Owner"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <TableHead>Client Base</TableHead>
                            <TableHead>Household</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoadingAccounts ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-muted-foreground text-center">
                                    Loading accounts...
                                </TableCell>
                            </TableRow>
                        ) : accounts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-muted-foreground text-center">
                                    No accounts without tenant assignment found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedAccounts.map((account) => (
                                <TableRow key={account.accountNumber}>
                                    <TableCell>{formatDate(account.tradeDate)}</TableCell>
                                    <TableCell className="font-mono text-sm">{account.accountNumber}</TableCell>
                                    <TableCell>
                                        {account.inherited ? 'Inherited ' : ''}
                                        {titleCase(account.accountType)}
                                    </TableCell>
                                    <TableCell>{getCustodianName(account)}</TableCell>
                                    <TableCell>{getAccountOwnerName(account)}</TableCell>
                                    <TableCell>
                                        {editingAccountNumber === account.accountNumber ? (
                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={selectedTenantGUID}
                                                    onValueChange={setSelectedTenantGUID}
                                                    disabled={isLoadingTenants || assignMutation.isPending}
                                                >
                                                    <SelectTrigger className="w-[180px]">
                                                        <SelectValue placeholder="Select tenant" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {tenants.map((tenant) => (
                                                            <SelectItem
                                                                key={tenant.tenantGUID}
                                                                value={tenant.tenantGUID}
                                                            >
                                                                {tenant.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleSave(account.accountNumber)}
                                                    disabled={!selectedTenantGUID || assignMutation.isPending}
                                                    className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                                                >
                                                    {assignMutation.isPending ? (
                                                        <LuLoader className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <LuCheck className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleCancel}
                                                    disabled={assignMutation.isPending}
                                                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                >
                                                    <LuX className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span>{getTenantName(account.tenantGUID)}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEditClick(account)}
                                                    className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                                                >
                                                    <LuPencil className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {account.tenantGUID && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setHouseholdModalAccount(account)}
                                                className="h-auto px-3 py-1 text-sm"
                                            >
                                                Assign
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setDismissModalAccount(account)}
                                            className="h-8 w-8 p-0 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                            aria-label={`Dismiss account ${account.accountNumber}`}
                                        >
                                            <LuArchiveX className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
            {householdModalAccount && (
                <AssignHouseholdModal account={householdModalAccount} onClose={() => setHouseholdModalAccount(null)} />
            )}
            {dismissModalAccount && (
                <ConfirmDialog
                    title="Dismiss Account Onboarding Record"
                    confirmLabel="Dismiss"
                    destructive
                    isPending={dismissMutation.isPending}
                    onConfirm={handleDismiss}
                    onCancel={() => setDismissModalAccount(null)}
                >
                    <p className="text-sm">
                        Dismiss account{' '}
                        <span className="font-mono font-medium">{dismissModalAccount.accountNumber}</span>? It will no
                        longer appear in account onboarding.
                    </p>
                </ConfirmDialog>
            )}
        </Card>
    );
};

const ConfirmDialog = ({
    title,
    children,
    confirmLabel,
    destructive = false,
    isPending,
    onConfirm,
    onCancel,
}: {
    title: string;
    children: React.ReactNode;
    confirmLabel: string;
    destructive?: boolean;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) => (
    <Dialog open onOpenChange={(open) => !open && !isPending && onCancel()}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            {children}
            <DialogFooter>
                <Button variant="outline" onClick={onCancel} disabled={isPending}>
                    Cancel
                </Button>
                <Button variant={destructive ? 'destructive' : 'default'} onClick={onConfirm} disabled={isPending}>
                    {isPending ? <LuLoader className="h-4 w-4 animate-spin" /> : confirmLabel}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);

const AssignHouseholdModal = ({ account, onClose }: { account: AccountOnboardingView; onClose: () => void }) => {
    const { data: households = [], isLoading } = useHouseholdsByTenantQuery(account.tenantGUID);
    const assignToHousehold = useAssignAccountToHouseholdAsAdmin();
    const queryClient = useQueryClient();
    const [selectedHouseholdGUID, setSelectedHouseholdGUID] = useState<string>('');
    const [showConfirm, setShowConfirm] = useState(false);
    const duplicates = usePotentialPersonDuplicates(
        JSON.stringify([account.accountNumber, account.tenantGUID, selectedHouseholdGUID]),
    );
    const sortedHouseholds = [...households].sort((a, b) => getHouseholdName(a).localeCompare(getHouseholdName(b)));
    const householdOptions = sortedHouseholds.map((household) => ({
        value: household.globalAccountGUID,
        label: getHouseholdName(household) || 'Unnamed Household',
    }));

    const selectedHousehold = households.find((h) => h.globalAccountGUID === selectedHouseholdGUID);

    const handleConfirm = () => {
        if (!selectedHouseholdGUID) return;
        void duplicates.check(
            () => checkAccountOwnerDuplicates(account),
            () =>
                assignToHousehold.mutate(
                    { accountNumber: account.accountNumber, householdGUID: selectedHouseholdGUID },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey: AccountOnboardingAdminQueryKeys.newAccountsNotYetOnboarded,
                            });
                            onClose();
                        },
                    },
                ),
        );
    };

    if (showConfirm) {
        return (
            <>
                <ConfirmDialog
                    title="Confirm Assignment"
                    confirmLabel="Confirm"
                    isPending={assignToHousehold.isPending || duplicates.busy}
                    onConfirm={handleConfirm}
                    onCancel={() => setShowConfirm(false)}
                >
                    <p className="text-sm">
                        Assign account <span className="font-mono font-medium">{account.accountNumber}</span> to{' '}
                        <span className="font-medium">{getHouseholdName(selectedHousehold)}</span>?
                    </p>
                </ConfirmDialog>
                <PotentialDuplicateDialog
                    result={duplicates.result}
                    onBack={duplicates.cancel}
                    onProceed={duplicates.proceed}
                    confirmText="Assign anyway"
                />
            </>
        );
    }

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Assign to Household</DialogTitle>
                </DialogHeader>
                <Combobox
                    options={householdOptions}
                    value={selectedHouseholdGUID}
                    onValueChange={setSelectedHouseholdGUID}
                    placeholder={isLoading ? 'Loading...' : 'Select a household'}
                    selectOptionLabel="Select a household"
                    noOptionsLabel="No households found"
                    disabled={isLoading}
                />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={() => setShowConfirm(true)} disabled={!selectedHouseholdGUID}>
                        OK
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
